import "./SubstationsLoss.css";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { BASE_URL } from "../../config";
import { toast } from "react-toastify";
import { GeoJsonLayer } from "@deck.gl/layers";
import "react-toastify/dist/ReactToastify.css";

const SELECTED_TS_COLOR = [255, 0, 0, 255];

const SubstationsLoss = ({
    clickInfo,
    selectedFeature,
    wfsLayers = [],
    setWfsLayers,
    handleSubstationsLossInformation,
    setOverlay,
}) => {
    const [substations, setSubstations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [checkedSubstations, setCheckedSubstations] = useState([]);
    const [selectedVod, setSelectedVod] = useState(null);

    const flatSelectedFeatures = useMemo(() => {
        if (!Array.isArray(selectedFeature)) return [];
        return selectedFeature.flat().filter(Boolean);
    }, [selectedFeature]);

    const getVodFromSelectedFeatures = useCallback(() => {
        const vodFeature = flatSelectedFeatures.find((feature) => {
            const featureId = feature?.id || "";
            const layerId = feature?.layer?.id || "";
            return featureId.includes("vod") || layerId === "vod-layer";
        });

        if (!vodFeature) return null;

        const props = vodFeature?.properties || vodFeature?.object?.properties || {};
        return props?.pripadnost_vodu ?? null;
    }, [flatSelectedFeatures]);

    useEffect(() => {
        const vodFromSelected = getVodFromSelectedFeatures();

        if (vodFromSelected) {
            setSelectedVod(vodFromSelected);
            return;
        }

        const isVodLayer = clickInfo?.layer?.id === "vod-layer";
        const pripadnostVodu = clickInfo?.object?.properties?.pripadnost_vodu;

        if (isVodLayer && pripadnostVodu) {
            setSelectedVod(pripadnostVodu);
            return;
        }

        setSelectedVod(null);
    }, [clickInfo, getVodFromSelectedFeatures]);

    useEffect(() => {
        const fetchContent = async () => {
            if (!selectedVod) {
                setSubstations([]);
                setCheckedSubstations([]);
                handleSubstationsLossInformation?.([]);
                setOverlay?.({ active: false, message: "" });
                return;
            }

            setIsLoading(true);
            setOverlay?.({ active: true, message: "Dohvatanje podataka" });

            try {
                const res = await axios.get(`${BASE_URL}/api/substations-loss`, {
                    params: { pripadnost_vodu: selectedVod },
                });

                const data = Array.isArray(res.data) ? res.data : [];
                setSubstations(data);
                setCheckedSubstations([]);
                handleSubstationsLossInformation?.([]);

                if (data.length === 0) {
                    toast.info("Nema trafostanica za izabrani vod.");
                }
            } catch (err) {
                console.error("Greška prilikom dohvaćanja trafo stanica:", err);
                toast.error("Greška prilikom dohvaćanja trafo stanica.");
                setSubstations([]);
                setCheckedSubstations([]);
                handleSubstationsLossInformation?.([]);
            } finally {
                setIsLoading(false);
                setOverlay?.({ active: false, message: "" });
            }
        };

        fetchContent();
    }, [selectedVod, handleSubstationsLossInformation, setOverlay]);

    const updateSelectedSubstationsColor = useCallback(
        (selectedNames) => {
            if (typeof setWfsLayers !== "function") return;

            setWfsLayers((prevLayers) =>
                prevLayers.map((layer) => {
                    if (!layer?.id?.includes("trafo_stanice")) {
                        return layer;
                    }

                    const originalData = layer?.props?.data;
                    if (!originalData?.features || !Array.isArray(originalData.features)) {
                        return layer;
                    }

                    const selectedSet = new Set(selectedNames);
                    const originalGetFillColor =
                        layer?.props?.getFillColor || (() => [255, 255, 255, 255]);

                    const newGetFillColor = (item) => {
                        const naziv =
                            item?.properties?.naziv ??
                            item?.properties?.naziv_ts ??
                            item?.properties?.Naziv ??
                            null;

                        if (naziv && selectedSet.has(naziv)) {
                            return SELECTED_TS_COLOR;
                        }

                        return typeof originalGetFillColor === "function"
                            ? originalGetFillColor(item)
                            : originalGetFillColor;
                    };

                    return new GeoJsonLayer({
                        ...layer.props,
                        id: layer.id,
                        data: originalData,
                        getFillColor: newGetFillColor,
                        updateTriggers: {
                            ...layer.props.updateTriggers,
                            getFillColor: [selectedNames.join("|")],
                        },
                    });
                })
            );
        },
        [setWfsLayers]
    );

    const handleCheckboxChange = (naziv) => {
        setCheckedSubstations((prev) =>
            prev.includes(naziv)
                ? prev.filter((item) => item !== naziv)
                : [...prev, naziv]
        );
    };

    useEffect(() => {
        if (!Array.isArray(substations) || substations.length === 0) {
            handleSubstationsLossInformation?.([]);
            updateSelectedSubstationsColor([]);
            return;
        }

        const selected = substations.filter((sub) =>
            checkedSubstations.includes(sub?.naziv)
        );

        handleSubstationsLossInformation?.(selected);
        updateSelectedSubstationsColor(checkedSubstations);
    }, [
        checkedSubstations,
        substations,
        handleSubstationsLossInformation,
        updateSelectedSubstationsColor,
    ]);

    const hasData = Array.isArray(substations) && substations.length > 0;

    return (
        <div className="substations-loss">
            <div className="substations-loss__wrapper">
                <h3 className="substations-loss__title">TS GUBICI</h3>

                {isLoading && <div className="substations-loss__spinner" />}

                {!isLoading && !selectedVod && (
                    <div className="substations-loss__info">
                        Nije izabran nijedan vod.
                    </div>
                )}

                {!isLoading && selectedVod && !hasData && (
                    <div className="substations-loss__info">
                        Nema trafostanica za izabrani vod.
                    </div>
                )}

                {!isLoading && hasData && (
                    <div className="substations-loss__content">
                        <div className="substations-loss__header">
                            <div />
                            <div className="substations-loss__label-naziv">Naziv TS</div>
                            <div className="substations-loss__label-gubici">Gubici [%]</div>
                        </div>

                        <div className="substations-loss__data">
                            {substations.map((substation, index) => (
                                <div
                                    className="substations-loss__row"
                                    key={substation?.id ?? `${substation?.naziv}-${index}`}
                                >
                                    <input
                                        className="substations-loss__checkbox"
                                        type="checkbox"
                                        checked={checkedSubstations.includes(substation?.naziv)}
                                        onChange={() => handleCheckboxChange(substation?.naziv)}
                                        disabled={!substation?.naziv}
                                    />

                                    <div className="substations-loss__value-naziv">
                                        {substation?.naziv || "Nema naziva"}
                                    </div>

                                    <div className="substations-loss__value-gubici">
                                        {substation?.gubitak ?? "Nema podatka"}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubstationsLoss;